import { DataGrid, GridColDef, GridRenderCellParams, GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';
import _ from 'lodash';
import { FunctionComponent, useMemo } from 'react';
import { QuestionConfig, QuestionId } from '../../types/GameplayTypes';
import { OrderButtons } from './OrderButtons';
import { QuestionActions } from './QuestionActions';
import { Author } from './QuestionComponents';
import { useQuestionFilters } from './QuestionFilterContext';
import { Tags } from './Tags';

/** Slightly larger than default. Allows for two rows of tags */
const ROW_HEIGHT = 60;

type QuestionSelectionListProps = {
	questions: QuestionConfig[];
	selectionModel: GridRowSelectionModel;
	onSelectionChange: (model: GridRowSelectionModel) => void;
	onEdit?: (question: QuestionConfig) => void;
	onDelete?: (id: QuestionId) => void;
};

/** List of questions for selection. Includes filtering and sorting. */
export const QuestionSelectionList: FunctionComponent<QuestionSelectionListProps> = (props) => {
	const { applyFilters, gridFilterModel, setGridFilterModel } = useQuestionFilters();

	const filteredQuestions = applyFilters(props.questions);
	const filteredQuestionIds = new Set<GridRowId>(filteredQuestions.map(q => q.id));

	// A selection model containing only visible (filtered) items
	const visibleSelectionModel: GridRowSelectionModel = {
		type: 'include',
		ids: new Set((Array.from(props.selectionModel.ids).filter(id => filteredQuestionIds.has(id)))),
	};

	function onEdit(id: QuestionId) {
		const question = props.questions.find(q => q.id === id);
		if (question && props.onEdit) {
			props.onEdit(question);
		}
	}

	function selectionChanged(newModel: GridRowSelectionModel) {
		// Preserve selections from filtered-out items
		const hiddenSelections = Array.from(props.selectionModel.ids).filter(id => !filteredQuestionIds.has(id));
		const combinedIds = new Set([...newModel.ids, ...hiddenSelections]);
		props.onSelectionChange({ type: 'include', ids: combinedIds });
	}

	const columns = useMemo<GridColDef[]>(() => [
		...sharedColumns(),
		...((props.onEdit || props.onDelete) ? [{
			field: 'actions',
			headerName: 'Actions',
			width: 120,
			sortable: false,
			renderCell: (params: GridRenderCellParams<QuestionConfig>) =>
				<QuestionActions id={params.row.id} itemName='Question' {...props} onEdit={onEdit} />
		}] : [])
	], [props.onDelete, props.onEdit]);

	return <DataGrid
		rows={filteredQuestions}
		columns={columns}
		initialState={{
			pagination: {
				paginationModel: { pageSize: 25 },
			},
			sorting: {
				sortModel: [{ field: 'isActive', sort: 'desc' }],
			},
		}}
		checkboxSelection
		disableRowSelectionOnClick
		rowSelectionModel={visibleSelectionModel}
		onRowSelectionModelChange={selectionChanged}
		filterModel={gridFilterModel}
		onFilterModelChange={setGridFilterModel}
		rowHeight={ROW_HEIGHT}
	/>;
};

type QuestionSetQuestionListProps = {
	questions: QuestionConfig[];
	selectionModel: GridRowSelectionModel;
	onSelectionChange: (model: GridRowSelectionModel) => unknown;
	onReorder?: (questionsId: QuestionId[]) => unknown;
};

/** 
 * List of questions from a question set. No filtering/sorting but questions are still selectable,
 * allowing for removal. Also features ordering controls to define round order.
 */
export const QuestionSetQuestionList: FunctionComponent<QuestionSetQuestionListProps> = (props) => {
	const visibleSelectionModel: GridRowSelectionModel = {
		type: 'include',
		ids: props.selectionModel.ids,
	};

	function selectionChanged(newModel: GridRowSelectionModel) {
		props.onSelectionChange(newModel);
	}

	function moveQuestion(id: QuestionId, direction: 'up' | 'down') {
		const currentPosition = props.questions.findIndex(q => q.id === id);
		const delta = (direction === 'up') ? - 1 : +1;
		const newPosition = _.clamp(currentPosition + delta, 0, props.questions.length - 1);

		if ((currentPosition !== -1) && (newPosition !== currentPosition)) {
			const reordered = props.questions.map(q => q.id);
			const [movedItem] = reordered.splice(currentPosition, 1);
			if (movedItem) {
				reordered.splice(newPosition, 0, movedItem);
				props.onReorder?.(reordered);
			}
		}
	}


	const columns = useMemo<GridColDef[]>(() => {
		return [{
			headerName: 'Order', field: 'reorder',
			headerAlign: 'center',
			align: 'center',
			sortable: false,
			renderCell: (params: GridRenderCellParams<QuestionConfig>) =>
				<OrderButtons questionId={params.row.id} allQuestions={props.questions} onMove={moveQuestion} />,
		},
		...sharedColumns(),
		];
	}, [props.questions, props.onReorder]);

	return <DataGrid
		rows={props.questions}
		columns={columns}
		initialState={{
			pagination: {
				paginationModel: { pageSize: 25 },
			},
		}}
		checkboxSelection
		disableColumnSorting
		disableColumnMenu
		disableRowSelectionOnClick
		rowSelectionModel={visibleSelectionModel}
		onRowSelectionModelChange={selectionChanged}
		rowHeight={ROW_HEIGHT}
	/>;
};

function sharedColumns(): GridColDef[] {
	return [{
		headerName: 'Question', field: 'text',
		flex: 1, minWidth: 300,
	}, {
		headerName: 'Tags', field: 'tags',
		width: 250,
		sortable: false,
		renderCell: (params: GridRenderCellParams<QuestionConfig>) => <Tags tags={params.row.tags} />
	}, {
		headerName: 'Active', field: 'isActive',
		type: 'boolean',
		width: 100,
	}, {
		headerName: 'Author',
		field: 'authorName',
		width: 200,
		renderCell: (params: GridRenderCellParams<QuestionConfig>) => <Author question={params.row} />
	}];
}
